/**
 * T247.G — Public scorer detail page.
 *
 * Route: /find-a-scorer/:id  (no auth required)
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft, BadgeCheck, MapPin, Calendar, Mail, Phone, ExternalLink,
} from "lucide-react";

import { getScorer } from "../../api/scorerMarketplace.api";


export default function ScorerDetail() {
  const { id } = useParams();
  const [scorer, setScorer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getScorer(id)
      .then(({ data }) => { if (alive) setScorer(data); })
      .catch(() => { if (alive) setError("Scorer not found."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  if (loading) return <div className="p-6 text-text-secondary">Loading…</div>;
  if (error || !scorer) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <p className="text-text-secondary mb-2">{error || "Not found."}</p>
        <Link to="/find-a-scorer" className="text-button-primary hover:opacity-80">
          ← Back to listings
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 text-text-primary">
      <Link
        to="/find-a-scorer"
        className="inline-flex items-center text-sm text-text-secondary hover:text-text-primary mb-3 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" /> Back to scorers
      </Link>

      <div className="border border-bg-secondary rounded-lg p-4 sm:p-6 bg-bg-primary mb-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {scorer.photo_url
            ? <img src={scorer.photo_url} alt={scorer.display_name}
                   className="w-32 h-32 rounded-full object-cover border border-bg-secondary self-center sm:self-auto" />
            : <div className="w-32 h-32 rounded-full bg-bg-main border border-bg-secondary flex items-center justify-center text-text-secondary self-center sm:self-auto">No photo</div>}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-text-primary break-words">{scorer.display_name}</h1>
              {scorer.has_verified_badge && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-button-primary/20 text-button-primary border border-button-primary/30 rounded text-xs font-medium">
                  <BadgeCheck className="w-4 h-4" />
                  Verified by ZCD
                </div>
              )}
            </div>
            <div className="text-sm text-text-secondary space-y-1">
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 shrink-0" /> {scorer.years_experience} years scoring
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 shrink-0" /> {(scorer.regions || []).join(", ") || "—"}
              </div>
              {scorer.daily_rate_inr && (
                <div className="text-text-primary">₹{scorer.daily_rate_inr} / day (indicative)</div>
              )}
            </div>

            <div className="flex flex-wrap gap-1 mt-3">
              {(scorer.formats_scored || []).map((f) => (
                <span key={f} className="text-xs px-2 py-0.5 bg-bg-secondary text-text-primary rounded">{f}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(scorer.languages || []).map((l) => (
                <span key={l} className="text-xs px-2 py-0.5 bg-button-primary/20 text-button-primary border border-button-primary/30 rounded uppercase">{l}</span>
              ))}
            </div>
          </div>
        </div>

        {scorer.bio && (
          <div className="mt-4 pt-4 border-t border-bg-secondary">
            <p className="text-sm text-text-secondary whitespace-pre-wrap break-words">{scorer.bio}</p>
          </div>
        )}
      </div>

      {/* Sample matches */}
      {scorer.sample_match_urls && scorer.sample_match_urls.length > 0 && (
        <div className="border border-bg-secondary rounded-lg p-4 bg-bg-primary mb-4">
          <h2 className="font-semibold mb-2 text-text-primary">Sample matches</h2>
          <ul className="space-y-1">
            {scorer.sample_match_urls.map((url, i) => (
              <li key={i}>
                <a href={url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-sm text-button-primary hover:underline break-all">
                  <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                  {url.replace(/^https?:\/\//, "").slice(0, 64)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Contact */}
      <div className="border border-bg-secondary rounded-lg p-4 bg-bg-primary">
        <h2 className="font-semibold mb-2 text-text-primary">Contact</h2>
        <div className="text-sm space-y-1">
          {scorer.contact_email && (
            <div className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-text-secondary shrink-0" />
              <a href={`mailto:${scorer.contact_email}`} className="text-button-primary hover:underline break-all">
                {scorer.contact_email}
              </a>
            </div>
          )}
          {scorer.contact_phone && (
            <div className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-text-secondary shrink-0" />
              <a href={`tel:${scorer.contact_phone}`} className="text-button-primary hover:underline">
                {scorer.contact_phone}
              </a>
            </div>
          )}
          {!scorer.contact_email && !scorer.contact_phone && (
            <p className="text-text-secondary">Contact details not provided.</p>
          )}
        </div>
      </div>

      <p className="mt-6 text-xs text-text-secondary text-center">
        ZenCricketData verifies basic information but doesn't manage hiring contracts.
        Please discuss terms directly with the scorer.
      </p>
    </div>
  );
}
